import { isNullOrUndefined } from "util";
import { CashFlowAdjustment } from "./CashFlowAdjustment";
import {isValidNumber, isVoid} from "projects/shared-components/utils";
import { StrategyPriceDto } from "projects/shared-components/shell-communication/shell-dto-protocol";
import { LastQuoteCacheService } from "projects/shared-components/last-quote-cache.service";
import * as Enumerable from "linq";


type IndexByCode = { [ix: string]: CashFlowAdjustment[] };

export class StrategyCodeIndex {
   constructor(private _lastQuoteCache: LastQuoteCacheService) {
   }

   //
   private _indexByStrategyCode: IndexByCode = {};
   private _indexByStrategyCodeZones: IndexByCode = {};
   private _indexByStrategyCodePricingSteps: IndexByCode = {};

   addData(adjustments: CashFlowAdjustment[]) {

      const toSubscribe = this.buildStrategyCodeIndex(adjustments);

      const strategyCodes = Enumerable
          .from(toSubscribe)
          .distinct()
          .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff([], strategyCodes);
   }

   addDataPricingSteps(adjustments: CashFlowAdjustment[]) {

      const toSubscribe = this.buildIndex(adjustments, this._indexByStrategyCodePricingSteps);

      const strategyCodes = Enumerable
          .from(toSubscribe)
          .distinct()
          .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff([], strategyCodes);
   }

   resetPricingSteps() {

      const strategyCodes = Enumerable
         .from(Object.keys(this._indexByStrategyCodePricingSteps))
         .distinct()
         .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff(strategyCodes, []);

      this._indexByStrategyCodePricingSteps = {};
   }

   addDataZones(adjustments: CashFlowAdjustment[]) {

      const toSubscribe = this.buildStrategyCodeIndexZones(adjustments);

      const strategyCodes = Enumerable
          .from(toSubscribe)
          .distinct()
          .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff([], strategyCodes);
   }

   removeData(adjustments: CashFlowAdjustment[]) {
      const toUnsubscribe = [];

      adjustments.forEach(adj => {

         if (isVoid(adj.optionStrategyCode)) {
            return;
         }

         let container = this._indexByStrategyCode[adj.optionStrategyCode];

         if (isNullOrUndefined(container)) {
            return;
         }

         const ix = container.indexOf(adj);

         if (ix < 0) {
            return;
         }

         container.splice(ix, 1);

         if (container.length === 0) {
            toUnsubscribe.push(adj.optionStrategyCode);
         }
      });

      this._lastQuoteCache.subscribeStrategyCodesDiff(toUnsubscribe, []);
   }

   //
   reset() {

      const strategyCodes = Enumerable
         .from(Object.keys(this._indexByStrategyCode))
         .distinct()
         .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff(strategyCodes, []);

      this._indexByStrategyCode = {};
   }

   resetZones() {

      const strategyCodes = Enumerable
         .from(Object.keys(this._indexByStrategyCodeZones))
         .distinct()
         .toArray();

      this._lastQuoteCache.subscribeStrategyCodesDiff(strategyCodes, []);

      this._indexByStrategyCodeZones = {};
   }

   //
   onStrategyPrice(prices: StrategyPriceDto[]): string[] {
      const main = this.onStrategyPriceIndex(prices, this._indexByStrategyCode);
      const zones = this.onStrategyPriceIndex(prices, this._indexByStrategyCodeZones);
      const pricingSteps = this.onStrategyPriceIndex(prices, this._indexByStrategyCodePricingSteps);

      return main.concat(zones).concat(pricingSteps);
   }

   private onStrategyPriceIndex(prices: StrategyPriceDto[], index: IndexByCode): string[] {

      if (!index) {
         return [];
      }

      if (Object.keys(prices).length === 0) {
         return [];
      }

      // let hasUpdates = false;
       const updates = [];

      prices.forEach(strategyPrice => {

         const container = index[strategyPrice.strategyCode];

         if (isNullOrUndefined(container)) {
            return;
         }

         if (container.length === 0) {
            return;
         }

         container.forEach(exp => {
            exp.price = isValidNumber(strategyPrice.price)
                ? (strategyPrice.price * -1) + (exp.marketDelta || 0)
                : null;
             updates.push(exp.optionStrategyCode);
         });
      });

      return updates;
   }

   //
   private buildStrategyCodeIndex(adjustments: CashFlowAdjustment[]): string[] {

      const toSubscribe = [];

      adjustments.forEach(adj => {

         if (isVoid(adj.optionStrategyCode)) {
            return;
         }

         let container = this._indexByStrategyCode[adj.optionStrategyCode];

         if (isNullOrUndefined(container)) {
            container = [];
            this._indexByStrategyCode[adj.optionStrategyCode] = container;
            toSubscribe.push(adj.optionStrategyCode);
         }

         container.push(adj);

         const lastStrategyPrice = this._lastQuoteCache.getLastStrategyPrice(adj.optionStrategyCode);
         if (!isVoid(lastStrategyPrice)) {
            this.onStrategyPrice([lastStrategyPrice]);
         }

      });

      return toSubscribe;

   }

   private buildStrategyCodeIndexZones(adjustments: CashFlowAdjustment[]): string[] {
      return this.buildIndex(adjustments, this._indexByStrategyCodeZones);
   }

   private buildIndex(adjustments: CashFlowAdjustment[], index: IndexByCode): string[] {

      const toSubscribe = [];

      adjustments.forEach(adj => {

         if (isVoid(adj.optionStrategyCode)) {
            return;
         }

         let container = index[adj.optionStrategyCode];

         if (isNullOrUndefined(container)) {
            container = [];
            index[adj.optionStrategyCode] = container;
            toSubscribe.push(adj.optionStrategyCode);
         }

         container.push(adj);

      });

      return toSubscribe;

   }

   isPriceBoxTheoretical() {
      return Object.keys(this._indexByStrategyCode)
          .some(x => x.indexOf(' !') >= 0);
   }

   isZonesGridTheoretical() {
      return Object.keys(this._indexByStrategyCodeZones)
          .some(x => x.indexOf(' !') >= 0);
   }
}

